import { Box, createStyles, makeStyles, TablePagination, Theme } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';

import { KeyboardShortcut } from '@/components/shared/components/KeyboardShortcut';
import { PendingDataWarning } from '@/components/shared/components/PendingDataWarning';
import { SearchResultCard } from '@/components/shared/components/SuperSearchBar/components/SearchResultCard/SearchResultCard';
import { SearchBarFilterOptions } from '@/components/shared/components/SuperSearchBar/types/SearchBarFilterOptions';
import { SearchResult } from '@/components/shared/components/SuperSearchBar/types/SearchResult';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { Platform } from '@/contexts/ConfigContext/ConfigContext';
import { useCustomTheme } from '@/hooks/useCustomTheme';

interface SearchResultListProps {
  requestLoading: boolean;
  selectedResult: number;
  searchResults?: SearchResult[];
  searchBarState: SearchBarFilterOptions;
  toggleDialog: () => void;
  onNavigate?: boolean;
  paginationProps: {
    page: number;
    itemCount: number;
    pageSize: number;
    handlePageChange: (event: any, newPage: any) => void;
    handlePageSizeChange: (event: any) => void;
  };
  platform?: Platform;
}

export const SearchResultList = ({
  requestLoading,
  searchResults,
  selectedResult,
  searchBarState,
  toggleDialog,
  onNavigate,
  paginationProps,
  platform,
}: SearchResultListProps) => {
  const { page, itemCount, handlePageChange, pageSize, handlePageSizeChange } = paginationProps;
  const { customBorderRadius } = useCustomTheme();
  const { getTextIdTranslation } = useTranslation();

  const classes = useStyles();

  if (!searchResults) {
    return null;
  }

  if (searchResults.length === 0 && !requestLoading) {
    return (
      <Box pb={'24px'}>
        <PendingDataWarning boxProps={{ className: 'fade-in-anim', height: 160 }} tId={'NO_RESULTS_TO_SHOW'} />
      </Box>
    );
  }

  return (
    <>
      <Box>
        {searchResults.map((searchResult, i) => (
          <Box mb={1} key={i}>
            {!requestLoading ? (
              <SearchResultCard
                selected={selectedResult === i}
                searchResult={searchResult}
                searchBarState={searchBarState}
                onNavigate={onNavigate}
                toggleDialog={toggleDialog}
              />
            ) : (
              <Skeleton
                className={'appear-anim'}
                variant={'rect'}
                height={'56px'}
                width={'100%'}
                style={{ borderRadius: customBorderRadius.paper }}
              />
            )}
          </Box>
        ))}
      </Box>
      <Box mt={'12px'} display={'flex'} justifyContent={'space-between'}>
        <Box display={'flex'} alignItems={'center'} gridGap={16}>
          <Box display={'flex'} alignItems={'center'} gridGap={4}>
            <KeyboardShortcut label={getTextIdTranslation('SELECT')} command="↑ / ↓" style={{ height: 12 }} />
            {platform && (
              <>
                <KeyboardShortcut command={platform === 'mac' ? 'Opt + ←' : 'Alt + ←'} style={{ height: 12 }} />
                <KeyboardShortcut command={platform === 'mac' ? 'Opt + →' : 'Alt + →'} style={{ height: 12 }} />
              </>
            )}
          </Box>
          <KeyboardShortcut
            label={getTextIdTranslation('OPEN')}
            command="Enter"
            iconName="keyboard_return"
            style={{ height: 12 }}
          />
        </Box>
        <TablePagination
          component="div"
          className={classes.toolbar}
          count={itemCount}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={pageSize}
          onRowsPerPageChange={handlePageSizeChange}
          labelRowsPerPage={''}
          rowsPerPageOptions={[8, 20, 50]}
        />
      </Box>
    </>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    toolbar: {
      '& .MuiIconButton-root': { padding: 4 },
      '& .MuiTablePagination-toolbar': { minHeight: 32 },
      '& .MuiTablePagination-selectRoot': { marginRight: 24 },
      '& .MuiSelect-select.MuiSelect-select': {
        fontSize: theme.typography.caption.fontSize,
        color: theme.palette.text.secondary,
      },
      '& .MuiTablePagination-caption': {
        fontSize: theme.typography.caption.fontSize,
        color: theme.palette.text.secondary,
      },
    },
  }),
);
